// Opportunity form component
import { useForm, Controller } from 'react-hook-form'
import { useQuery } from '@tanstack/react-query'
import { useEffect } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { opportunitySchema, type OpportunityInput, type Account, type Contact, type Address } from '../../../shared/types'
import { useTranslation } from '../../hooks/useTranslation'
import { Autocomplete, type AutocompleteOption } from '../ui/Autocomplete'
import { createCrudApi, type PaginatedResponse } from '../../lib/api'
import { OwnerSelect } from './OwnerSelect'

interface Props {
  defaultValues?: Partial<OpportunityInput>
  onSubmit: (data: OpportunityInput) => void
  onCancel?: () => void
  loading?: boolean
  accountId?: string
  accountName?: string
  canWriteField?: (field: string) => boolean
}

const accountsApi = createCrudApi<Account, unknown>('accounts')
const contactsApi = createCrudApi<Contact, unknown>('contacts')
const addressesApi = createCrudApi<Address, unknown>('addresses')

function formatAddress(address: Address): string {
  return `${address.street}, ${address.city}${address.state ? `, ${address.state}` : ''} ${address.postalCode || ''}, ${address.country}`
}

async function searchAccounts(search: string): Promise<AutocompleteOption[]> {
  const response: PaginatedResponse<Account> = await accountsApi.list({
    filters: { name: search },
    limit: 10,
  })
  return response.data.map((account) => ({
    id: account.id,
    label: account.name,
  }))
}

export function OpportunityForm({ defaultValues, onSubmit, onCancel, loading, accountId, accountName, canWriteField }: Props) {
  // Default to allowing all writes if FLS not provided
  const canWrite = canWriteField ?? (() => true)
  const { t } = useTranslation()
  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm<OpportunityInput>({
    resolver: zodResolver(opportunitySchema),
    defaultValues: accountId ? { ...defaultValues, accountId } : defaultValues,
  })

  // Watch the accountId to fetch related contacts
  const selectedAccountId = watch('accountId') || accountId

  // Fetch contacts for the selected account
  const { data: contactsResponse, isLoading: contactsLoading } = useQuery({
    queryKey: ['contacts', 'byAccount', selectedAccountId],
    queryFn: () => contactsApi.list({ filters: { accountId: selectedAccountId! }, limit: 100 }),
    enabled: !!selectedAccountId,
  })

  const contacts = contactsResponse?.data || []

  // Fetch addresses for the selected account (shipping addresses)
  const { data: addressesResponse, isLoading: addressesLoading } = useQuery({
    queryKey: ['addresses', 'byAccount', selectedAccountId],
    queryFn: () => addressesApi.list({ filters: { accountId: selectedAccountId! }, limit: 50 }),
    enabled: !!selectedAccountId,
  })

  // Filter for shipping addresses, but also include primary as fallback
  const shippingAddresses = addressesResponse?.data?.filter(a => a.type === 'shipping') || []
  const primaryAddress = addressesResponse?.data?.find(a => a.type === 'primary')

  // Clear contactId when account changes (except on initial load)
  useEffect(() => {
    if (selectedAccountId && selectedAccountId !== defaultValues?.accountId) {
      setValue('contactId', undefined)
    }
  }, [selectedAccountId, setValue, defaultValues?.accountId])

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <div>
        <label htmlFor="name" className="block text-sm font-medium text-gray-700">
          {t('fields.opportunityName')} <span className="text-red-500">*</span>
        </label>
        <input
          {...register('name')}
          type="text"
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
        />
        {errors.name && (
          <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
        )}
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div>
          <label htmlFor="amount" className="block text-sm font-medium text-gray-700">
            {t('fields.amount')}
            {!canWrite('amount') && <span className="ml-1 text-xs text-gray-400">(read only)</span>}
          </label>
          <input
            {...register('amount')}
            type="text"
            placeholder="0.00"
            disabled={!canWrite('amount')}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm disabled:bg-gray-100 disabled:text-gray-500"
          />
          {errors.amount && (
            <p className="mt-1 text-sm text-red-600">{errors.amount.message}</p>
          )}
        </div>

        <div>
          <label htmlFor="stage" className="block text-sm font-medium text-gray-700">
            {t('fields.stage')}
            {!canWrite('stage') && <span className="ml-1 text-xs text-gray-400">(read only)</span>}
          </label>
          <select
            {...register('stage')}
            disabled={!canWrite('stage')}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm disabled:bg-gray-100 disabled:text-gray-500"
          >
            <option value="qualification">{t('status.qualification')}</option>
            <option value="proposal">{t('status.proposal')}</option>
            <option value="negotiation">{t('status.negotiation')}</option>
            <option value="closed_won">{t('status.closedWon')}</option>
            <option value="closed_lost">{t('status.closedLost')}</option>
          </select>
          {errors.stage && (
            <p className="mt-1 text-sm text-red-600">{errors.stage.message}</p>
          )}
        </div>

        <div>
          <label htmlFor="probability" className="block text-sm font-medium text-gray-700">
            {t('fields.probability')}
            {!canWrite('probability') && <span className="ml-1 text-xs text-gray-400">(read only)</span>}
          </label>
          <select
            {...register('probability')}
            disabled={!canWrite('probability')}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm disabled:bg-gray-100 disabled:text-gray-500"
          >
            <option value="">{t('common.select')}</option>
            <option value="10%">10%</option>
            <option value="25%">25%</option>
            <option value="50%">50%</option>
            <option value="75%">75%</option>
            <option value="90%">90%</option>
          </select>
          {errors.probability && (
            <p className="mt-1 text-sm text-red-600">{errors.probability.message}</p>
          )}
        </div>
      </div>

      <div>
        <label htmlFor="closeDate" className="block text-sm font-medium text-gray-700">
          {t('fields.closeDate')} <span className="text-red-500">*</span>
        </label>
        <input
          {...register('closeDate')}
          type="datetime-local"
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
        />
        {errors.closeDate && (
          <p className="mt-1 text-sm text-red-600">{errors.closeDate.message}</p>
        )}
      </div>

      {!accountId && (
        <Controller
          name="accountId"
          control={control}
          render={({ field }) => (
            <Autocomplete
              value={field.value || ''}
              onChange={(id) => field.onChange(id)}
              fetchOptions={searchAccounts}
              label={t('fields.account')}
              placeholder="Type account name..."
              required
              error={errors.accountId?.message}
              minChars={2}
              queryKey="accounts"
              initialLabel={accountName}
            />
          )}
        />
      )}

      <div>
        <label htmlFor="contactId" className="block text-sm font-medium text-gray-700">
          {t('fields.contact')}
        </label>
        <select
          {...register('contactId')}
          disabled={!selectedAccountId || contactsLoading}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm disabled:bg-gray-100 disabled:text-gray-500"
        >
          <option value="">
            {!selectedAccountId
              ? 'Select an account first...'
              : contactsLoading
              ? 'Loading contacts...'
              : contacts.length === 0
              ? 'No contacts for this account'
              : 'Select a contact...'}
          </option>
          {contacts.map((contact) => (
            <option key={contact.id} value={contact.id}>
              {[contact.firstName, contact.lastName].filter(Boolean).join(' ')}
              {contact.title ? ` - ${contact.title}` : ''}
            </option>
          ))}
        </select>
        {errors.contactId && (
          <p className="mt-1 text-sm text-red-600">{errors.contactId.message}</p>
        )}
      </div>

      {/* Shipping Address Section */}
      {selectedAccountId && (
        <div className="border-t border-gray-200 pt-4">
          <label htmlFor="shippingAddressId" className="block text-sm font-medium text-gray-700">
            {t('fields.shipTo')}
          </label>
          <select
            {...register('shippingAddressId')}
            disabled={addressesLoading}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm disabled:bg-gray-100 disabled:text-gray-500"
          >
            {primaryAddress && (
              <option value="">
                Default: {formatAddress(primaryAddress)}
              </option>
            )}
            {!primaryAddress && (
              <option value="">No default address</option>
            )}
            {shippingAddresses.map((address) => (
              <option key={address.id} value={address.id}>
                {formatAddress(address)}
              </option>
            ))}
          </select>
          {shippingAddresses.length === 0 && !primaryAddress && (
            <p className="mt-1 text-sm text-gray-500">
              No shipping addresses found. Add addresses on the account page first.
            </p>
          )}
          {errors.shippingAddressId && (
            <p className="mt-1 text-sm text-red-600">{errors.shippingAddressId.message}</p>
          )}
        </div>
      )}

      {/* Owner assignment - only visible to admins/managers */}
      <Controller
        name="ownerId"
        control={control}
        render={({ field }) => (
          <OwnerSelect
            value={field.value}
            onChange={field.onChange}
            disabled={loading}
          />
        )}
      />

      <div className="flex justify-end gap-3 pt-4">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            {t('common.cancel')}
          </button>
        )}
        <button
          type="submit"
          disabled={loading}
          className="rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
        >
          {loading ? t('common.saving') : t('common.save')}
        </button>
      </div>
    </form>
  )
}
